import { LocateFixed, Map as MapIcon, MapPin } from "lucide-react";
import { type FocusEvent, type KeyboardEvent, type ReactNode, useEffect, useId, useMemo, useRef, useState } from "react";

import type { NamedPlace, WeatherCustom, ZoneLetter } from "../api/types";
import type { Awaiting } from "../components/awaiting";
import { Spinner } from "../components/EmptyState";
import { PanelRow } from "../components/panel";
import { Motion, Presence } from "../components/presence";
import { inputClass, SearchField, SectionLabel } from "../components/ui";
import { Waiting } from "../components/Waiting";
import { t } from "../i18n/i18n";
import {
  formatLatLon,
  groupSuggestions,
  type KnownMap,
  PlaceSuggester,
  type Suggestion,
  type SuggestState,
  SUGGEST_LABELS,
} from "../map/zones/placeSuggest";
import { estimatedClimate, useWeatherOptions } from "./WeatherField";

/** Cible `data-attention` du champ (étape « Lieu de l'exercice » des Données initiales). */
export const PLACE_FIELD = "dossier-place";

/** Lettres de l'heure légale d'une localité choisie (le parent en tire le fuseau proposé). */
export interface PlaceZones {
  winter: string;
  summer: string;
}

/** Champ « Lieu du nouveau terrain » (rubrique « Lieu de l'exercice » des Données initiales, sous « Terrain :
 *  Nouveau terrain ») : ville ou localité proche, coordonnées ou MGRS. Il situe le climat du terrain avant son
 *  placement : la météo « Choisie » se règle tout de suite, sans quitter les Données initiales, et la vue Manœuvre se
 *  centrera sur ce lieu. Suggestions sans appel extérieur (`placeSuggest`) : les terrains de la licence (les choisir
 *  reprend ce terrain), les espaces d'entraînement (les choisir passe sur ce camp), les localités du répertoire du
 *  serveur, les coordonnées reconnues sur le poste. Le texte n'est retenu qu'une fois un lieu choisi ; quitté sans
 *  choix, le champ reprend le lieu retenu, ou dit d'en choisir un. Sous le champ, une fois choisi : le climat estimé et
 *  la position (« Climat estimé : semi-continental · 58,38° N 26,72° E »). Lecture seule : le nom se lit, l'appui
 *  désigne les droits. */
export function PlaceField({
  value,
  onChange,
  terrains,
  camps,
  onTerrain,
  onCamp,
  country,
  weather,
  readOnly,
  conflict,
  awaiting,
}: {
  value: NamedPlace | null;
  /** Lieu choisi (null : effacé), et l'heure légale d'une localité du répertoire. */
  onChange: (place: NamedPlace | null, zones: PlaceZones | null) => void;
  terrains: readonly KnownMap[];
  camps: readonly KnownMap[];
  onTerrain: (slug: string) => void;
  onCamp: (slug: string) => void;
  /** Pays de la force (ISO 3166-1 alpha-2) : ses localités passent avant leurs homonymes. */
  country?: string;
  /** Météo du brouillon : le climat estimé se lit sur la même réponse que le réglage Météo (une seule requête). */
  weather: { startDtg: string | null; timeZone: ZoneLetter | null; scenario: string | null; custom: WeatherCustom | null | undefined };
  readOnly?: () => void;
  conflict?: ReactNode;
  /** Lieu attendu (nouveau terrain sans lieu) : surbrillance du champ tant qu'aucun lieu n'est choisi
   *  (`components/awaiting.ts`). */
  awaiting?: Awaiting;
}) {
  const [text, setText] = useState(value?.name ?? "");
  const [state, setState] = useState<SuggestState>({ status: "idle" });
  const [active, setActive] = useState(0);
  // Saisie quittée sans choix et sans lieu retenu : la phrase dit d'en choisir un.
  const [unchosen, setUnchosen] = useState(false);
  const listId = useId();
  const boxRef = useRef<HTMLDivElement>(null);
  const maps = useRef({ terrains, camps });
  maps.current = { terrains, camps };
  const countryRef = useRef(country);
  countryRef.current = country;
  const suggester = useMemo(
    () => new PlaceSuggester({ onState: setState, maps: () => maps.current, country: () => countryRef.current }),
    [],
  );
  useEffect(() => () => suggester.cancel(), [suggester]);
  // Lieu changé d'ailleurs (« Annuler les modifications », report, autre dossier) : le champ le montre.
  useEffect(() => {
    setText(value?.name ?? "");
    setUnchosen(false);
  }, [value?.name, value?.lon, value?.lat]);

  const { options } = useWeatherOptions({ mapSlug: null, place: value, ...weather });
  const items = state.status === "done" ? state.items : state.status === "loading" || state.status === "unavailable" ? state.local : [];
  const groups = groupSuggestions(items);
  const flat = groups.flatMap((g) => g.items);

  const close = () => {
    suggester.cancel();
    setState({ status: "idle" });
  };
  const choose = (s: Suggestion) => {
    close();
    setUnchosen(false);
    if ("slug" in s) {
      if (s.group === "terrains") onTerrain(s.slug);
      else onCamp(s.slug);
      return;
    }
    setText(s.place.name);
    onChange(s.place, s.zones ?? null);
  };
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!flat.length) return;
    if (e.key === "Escape") {
      // Échap ferme d'abord la liste (le texte reste) ; un second Échap efface le champ.
      e.preventDefault();
      e.stopPropagation();
      close();
    } else if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => (a + (e.key === "ArrowDown" ? 1 : flat.length - 1)) % flat.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const s = flat[Math.min(active, flat.length - 1)];
      if (s) choose(s);
    }
  };
  // Champ quitté (ni la liste ni le champ n'ont plus le focus) : le lieu retenu revient, sinon la phrase le demande.
  const onBlur = (e: FocusEvent<HTMLDivElement>) => {
    if (e.relatedTarget instanceof Node && boxRef.current?.contains(e.relatedTarget)) return;
    close();
    if (value) setText(value.name);
    else setUnchosen(Boolean(text.trim()));
  };

  if (readOnly) {
    return (
      <div data-attention={PLACE_FIELD} className="space-y-1 rounded-lg">
        <span className="mb-1 block text-ui font-medium text-ink-soft">{t("Lieu du nouveau terrain")}</span>
        <input className={inputClass} value={value?.name ?? ""} readOnly onClick={readOnly} aria-label={t("Lieu du nouveau terrain")} />
        {value && <PlaceLine place={value} climate={options?.climate ?? null} />}
      </div>
    );
  }

  const unavailable = state.status === "unavailable";
  const line = value && !unchosen && text === value.name;
  let index = -1;
  return (
    <div ref={boxRef} data-attention={PLACE_FIELD} className="space-y-1.5 rounded-lg" onBlur={onBlur}>
      <div>
        <span className="mb-1 block text-ui font-medium text-ink-soft">{t("Lieu du nouveau terrain")}</span>
        <SearchField
          value={text}
          onChange={(next) => {
            setText(next);
            setActive(0);
            setUnchosen(false);
            suggester.input(next);
          }}
          onClear={() => {
            setText("");
            close();
            setUnchosen(false);
            if (value) onChange(null, null);
          }}
          onKeyDown={onKeyDown}
          combobox={{ listId, expanded: flat.length > 0, activeId: flat.length ? `${listId}-${Math.min(active, flat.length - 1)}` : undefined }}
          placeholder={t("Ville, coordonnées ou MGRS")}
          ariaLabel={t("Lieu du nouveau terrain")}
          awaiting={awaiting}
        />
      </div>
      {conflict}
      <Presence show={state.status === "loading"}>
        <Motion origin="fade" className="flex min-h-8 items-center gap-2 px-1 text-meta text-ink-muted">
          <Spinner decorative /> <Waiting>{t("Recherche…")}</Waiting>
        </Motion>
      </Presence>
      <Presence show={state.status === "done" && items.length === 0}>
        <Motion origin="fade" className="px-1 text-meta text-ink-muted">
          {t("Aucun lieu trouvé : essayez une ville proche, ou saisissez des coordonnées.")}
        </Motion>
      </Presence>
      <Presence show={flat.length > 0}>
        <Motion origin="fade" role="listbox" id={listId} aria-label={t("Lieux trouvés")} className="space-y-1">
          {groups.map((group) => (
            <div key={group.group} role="group" aria-label={t(SUGGEST_LABELS[group.group])}>
              <SectionLabel inset className="px-2 pt-1 pb-0.5">
                {t(SUGGEST_LABELS[group.group])}
              </SectionLabel>
              {group.items.map((s) => {
                index += 1;
                const i = index;
                return (
                  <PanelRow
                    key={s.id}
                    icon={<SuggestionIcon group={s.group} />}
                    title={s.name}
                    detail={s.detail}
                    option={{ id: `${listId}-${i}`, active: i === active }}
                    onClick={() => choose(s)}
                  />
                );
              })}
            </div>
          ))}
        </Motion>
      </Presence>
      {/* Une seule phrase sous le champ : le lieu retenu, ou ce qu'il faut faire. */}
      <Motion
        key={line ? "place" : unavailable ? "unavailable" : state.status === "outside" ? "outside" : unchosen ? "unchosen" : state.status === "short" ? "short" : "help"}
        origin="fade"
      >
        {line ? (
          <PlaceLine place={value!} climate={options?.climate ?? null} />
        ) : unavailable ? (
          <p className="px-1 text-meta leading-snug text-warn">{t("Recherche de lieux indisponible sur ce serveur : saisissez des coordonnées ou un MGRS.")}</p>
        ) : state.status === "outside" ? (
          <p className="px-1 text-meta leading-snug text-warn">{t("Lieu hors des limites du terrain libre (79,5° S à 83,5° N).")}</p>
        ) : unchosen ? (
          <p className="px-1 text-meta leading-snug text-warn">{t("Choisissez un lieu dans la liste, ou saisissez des coordonnées.")}</p>
        ) : state.status === "short" ? (
          <p className="px-1 text-meta leading-snug text-ink-muted">{t("Trois caractères au moins.")}</p>
        ) : (
          <p className="px-1 text-meta leading-snug text-ink-muted">
            {t("Ville ou localité proche, coordonnées ou MGRS : la carte s'y centrera et la météo suivra son climat.")}
          </p>
        )}
      </Motion>
    </div>
  );
}

function SuggestionIcon({ group }: { group: Suggestion["group"] }) {
  if (group === "coords") return <LocateFixed size={14} />;
  // Terrains de la licence et camps : la carte, comme leurs lignes de la vue Manœuvre.
  if (group === "terrains" || group === "camps") return <MapIcon size={14} />;
  return <MapPin size={14} />;
}

/** Ligne grise du lieu retenu : région et pays, climat estimé (dès que la météo du lieu est lue) et position. */
function PlaceLine({ place, climate }: { place: NamedPlace; climate: string | null }) {
  const position = formatLatLon(place.lat, place.lon);
  return (
    <p className="px-1 text-meta leading-snug text-ink-muted">
      {place.region && <span className="block text-ink-soft">{place.region}</span>}
      <span className="tabular-nums">
        {climate ? t("Climat estimé : {climat} · {position}", { climat: estimatedClimate(climate), position }) : position}
      </span>
    </p>
  );
}
